// One box, four kinds of answer.
//
// The question somebody has is "where is `delta_vs_default`" or "what do we
// have on budget_v3", and they do not know in advance whether the answer is a
// column, a view, a dashboard somebody already built, or an experiment key.
// Four filtered lists would make them guess the category first, which is the
// guess they opened the box to avoid. `GET /api/bi/search` ranks all four
// together; this component is the keyboard around it.
//
// **Keyboard first, deliberately.** It opens on `/` or cmd+K from anywhere in
// the app, the input takes focus on open, up/down move through the results,
// Enter follows the highlighted one and Escape closes. A palette you have to
// point at is a menu, and a menu is what the header already has.
//
// **Every result goes somewhere useful.** A source or a column opens the canvas
// on that source (a column additionally names itself in the URL so the rail
// scrolls to it), a dashboard opens itself, an experiment opens its page. A hit
// that is only a label would make the box a lookup table rather than a way to
// get to work.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import type { BiSearchHit } from "@bi/shared/types";

import { biSearchQuery } from "../lib/queries";

const KIND_LABELS: Record<BiSearchHit["kind"], string> = {
  source: "Source",
  column: "Field",
  dashboard: "Dashboard",
  experiment: "Experiment",
};

export function SearchPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const search = useQuery(biSearchQuery(query));
  const results = useMemo(() => search.data?.results ?? [], [search.data]);

  // Open from anywhere. `/` is the convention every search box in a read-heavy
  // app uses, and it is ignored while somebody is typing into a field — taking
  // a slash out of a chart title would be worse than having no shortcut.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["input", "textarea", "select"].includes(target.tagName.toLowerCase()));
      if ((event.key === "k" && (event.metaKey || event.ctrlKey)) || (event.key === "/" && !typing)) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const go = (hit: BiSearchHit) => {
    setOpen(false);
    setQuery("");
    switch (hit.kind) {
      case "dashboard":
        void navigate({ to: "/studio/$id", params: { id: hit.id }, search: {} });
        return;
      case "experiment":
        void navigate({ to: "/experiments/$experimentKey", params: { experimentKey: hit.id } });
        return;
      case "source":
      case "column":
        void navigate({
          to: "/studio/new",
          search: {
            source: hit.sourceId ?? undefined,
            field: hit.column ?? undefined,
          },
        });
        return;
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => (c + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => (c - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = results[cursor];
      if (hit) go(hit);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="bi-search__opener"
        onClick={() => setOpen(true)}
        title="Search sources, fields, dashboards and experiments. Press / anywhere."
      >
        Search
        <span className="bi-search__hint" aria-hidden="true">
          /
        </span>
      </button>
    );
  }

  return (
    <div className="bi-search" role="dialog" aria-label="Search" onKeyDown={onKeyDown}>
      <div className="bi-search__backdrop" onClick={() => setOpen(false)} />
      <div className="bi-search__panel">
        <input
          ref={inputRef}
          className="bi-search__input"
          type="search"
          value={query}
          placeholder="A field, a source, a dashboard, an experiment"
          aria-label="Search"
          onChange={(e) => setQuery(e.target.value)}
        />
        {query.trim().length < 2 ? (
          <p className="bi-search__note">
            Type at least two characters. Field descriptions are searched as well as names, so
            "how many units" finds the column that counts them.
          </p>
        ) : null}
        {search.isError ? (
          <p className="bi-search__note" role="alert">
            Search is not answering right now.
          </p>
        ) : null}
        {query.trim().length >= 2 && !search.isPending && results.length === 0 ? (
          <p className="bi-search__note">Nothing matches that.</p>
        ) : null}
        <ul className="bi-search__results">
          {results.map((hit, index) => (
            <li key={`${hit.kind}:${hit.id}`}>
              <button
                type="button"
                className={`bi-search__result${index === cursor ? " bi-search__result--active" : ""}`}
                onMouseEnter={() => setCursor(index)}
                onClick={() => go(hit)}
              >
                <span className="bi-search__kind">{KIND_LABELS[hit.kind]}</span>
                <span className="bi-search__name">{hit.name}</span>
                <span className="bi-search__detail">
                  {hit.kind === "column" && hit.sourceId ? `${hit.sourceId}. ` : ""}
                  {hit.detail}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {search.data?.truncated ? (
          // Said out loud: a capped list looks exactly like a complete one, and
          // the hit somebody wanted may be the one that was dropped.
          <p className="bi-search__note">
            More matched than fit here. Add a word to narrow it.
          </p>
        ) : null}
      </div>
    </div>
  );
}
